// frontend/src/components/auth/StepUpProvider.tsx
//
// Bridges the api.js step-up handler hook (setStepUpHandler) with the
// StepUpModal UI. Mount once near the root of the authenticated tree
// (typically inside AppLayout) so a 401 STEP_UP_REQUIRED from anywhere in
// the app pops the same modal.
//
// Also runs a one-shot status check on mount so a page refresh while sudo
// is active surfaces the SudoBanner immediately.

import type { ReactNode } from 'react'
import { useEffect, useRef, useState, useCallback } from 'react'
import StepUpModal from './StepUpModal.jsx'
import api, { setStepUpHandler } from '../../utils/api.js'
import useSudoStore from '../../store/sudoStore.js'

interface StepUpProviderProps {
  children: ReactNode
}

interface ModalState {
  open: boolean
  scope: string | null
}

interface PendingRef {
  resolve: () => void
  reject: (reason?: unknown) => void
}

export default function StepUpProvider({ children }: StepUpProviderProps) {
  const setActive = useSudoStore(s => s.setActive)
  const clear = useSudoStore(s => s.clear)

  const [modal, setModal] = useState<ModalState>({ open: false, scope: null })
  const pendingRef = useRef<PendingRef | null>(null)

  // Register the singleton handler. api.js calls it with { scope } and
  // expects a promise; we resolve it from the modal's onConfirm.
  useEffect(() => {
    setStepUpHandler(({ scope } = { scope: null }) => {
      // If a previous request is still mid-flight, reject it before opening
      // a fresh one — only one step-up modal at a time.
      if (pendingRef.current) {
        try { pendingRef.current.reject(new Error('Superseded by new step-up request')) } catch { /* already settled — ignore */ }
        pendingRef.current = null
      }
      setModal({ open: true, scope: scope || null })
      return new Promise<unknown>((resolve, reject) => {
        pendingRef.current = { resolve: resolve as () => void, reject }
      })
    })
    return () => setStepUpHandler(null)
  }, [])

  // One-shot status hydration. Silently tolerate 401 — that just means the
  // user isn't logged in yet (StepUpProvider also mounts on public surfaces
  // if it's high in the tree).
  useEffect(() => {
    let cancelled = false
    api.get<{ active?: boolean; expires_at?: string; scopes?: string[] }>('/auth/step-up/status')
      .then(({ data }) => {
        if (cancelled) return
        if (data?.active) {
          setActive({ expires_at: data.expires_at, scopes: data.scopes })
        } else {
          clear()
        }
      })
      .catch(() => { /* swallow — pre-auth or transient */ })
    return () => { cancelled = true }
  }, [setActive, clear])

  const onConfirm = useCallback(() => {
    const p = pendingRef.current
    pendingRef.current = null
    setModal({ open: false, scope: null })
    p?.resolve?.()
  }, [])

  const onCancel = useCallback(() => {
    const p = pendingRef.current
    pendingRef.current = null
    setModal({ open: false, scope: null })
    p?.reject?.(new Error('User cancelled step-up'))
  }, [])

  return (
    <>
      {children}
      <StepUpModal
        open={modal.open}
        scope={modal.scope}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </>
  )
}
